import { useEffect, useRef, useState } from "react";

/**
 * Standalone Home page for the MenteSquare PeopleProcess microsite.
 *
 * Ported from the original static public/site.html into a self-contained
 * React component, following the same pattern as ContactPage.tsx:
 *  - all CSS is scoped under #home-page and injected via a <style> tag
 *  - behaviour that was vanilla inline <script> (nav scroll state, hamburger
 *    menu, module/FAQ accordions, testimonial/logo carousel, reveal-on-scroll,
 *    page loader, hero "reserve seat" form submit) is reimplemented with
 *    React hooks
 *  - the component has no dependency on Tailwind, Radix, or any UI kit —
 *    just React — so it can be dropped into another React app directly.
 */

const HOME_STYLES = `
 #home-page {
      --teal: #00897B;
      --teal-dark: #00695C;
      --teal-light: #B2DFDB;
      --teal-glow: #26A69A;
      --off: #F9FAFB;
      --ink: #1C1C2E;
      --ink-soft: #2A2A40;
      --muted: #6B7280;
      --grad: linear-gradient(135deg, #00897B 0%, #26A69A 50%, #4DB6AC 100%);
      --grad-dark: linear-gradient(135deg, #E0F2F1 0%, #B2DFDB 100%);
      --glass: rgba(255, 255, 255, 0.08);
      --glass-border: rgba(255, 255, 255, 0.18);
      --shadow: 0 20px 60px -20px rgba(0, 137, 123, .35);
      --radius: 16px;
      --gold: #F5A623;
      --gold-dark: #E08E0B;
      --gold-grad: linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%);
    }
 #home-page * {
      box-sizing: border-box;
      margin: 0;
      padding: 0
    }
 #home-page {
      scroll-behavior: smooth;
      scroll-padding-top: 80px;
      overflow-x: hidden;
      max-width: 100%
    }
 #home-page {
      font-family: "Inter", system-ui, sans-serif;
      color: var(--ink);
      background: var(--off);
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
      overflow-x: hidden;
      max-width: 100%;
      position: relative
    }
 #home-page h1, #home-page h2, #home-page h3, #home-page h4 {
      font-family: "Syne", sans-serif;
      font-weight: 700;
      line-height: 1.15;
      letter-spacing: -.02em;
      color: var(--ink)
    }
 #home-page h1 {
      font-size: clamp(2.2rem, 5.2vw, 4rem)
    }
 #home-page h2 {
      font-size: clamp(1.8rem, 3.8vw, 2.8rem)
    }
 #home-page h3 {
      font-size: 1.25rem
    }
 #home-page p {
      color: var(--muted)
    }
 #home-page a {
      color: inherit;
      text-decoration: none
    }
 #home-page img {
      max-width: 100%;
      display: block
    }
 #home-page .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 24px
    }
 #home-page .section {
      padding: 96px 0;
      position: relative;
      overflow: hidden
    }
 #home-page .hero .badge {
      display: table;
      margin-left: auto;
      margin-right: auto;
    }
 #home-page .eyebrow {
      display: inline-block;
      font-size: .95rem;
      
      font-weight: 800;
      
      letter-spacing: .16em;
      
      text-transform: uppercase;
      color: var(--teal);
      margin-bottom: 14px
    }
 #home-page .btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 14px 26px;
      border-radius: 999px;
      font-weight: 600;
      font-size: .95rem;
      cursor: pointer;
      border: none;
      transition: transform .25s ease, box-shadow .25s ease, background .25s ease;
      font-family: inherit
    }
 #home-page .btn-primary {
      background: var(--teal);
      color: #fff;
      box-shadow: 0 10px 24px -8px rgba(0, 137, 123, .5)
    }
 #home-page .btn-primary:hover {
      transform: translateY(-2px);
      background: var(--teal-dark);
      box-shadow: 0 16px 32px -10px rgba(0, 137, 123, .6)
    }
 #home-page .btn-outline {
      background: transparent;
      color: var(--ink);
      border: 1.5px solid rgba(0, 0, 0, .2)
    }
 #home-page .btn-outline:hover {
      background: rgba(0, 137, 123, .08);
      border-color: var(--teal)
    }
 #home-page .btn-ghost {
      background: transparent;
      color: var(--teal);
      border: 1.5px solid var(--teal)
    }
 #home-page .btn-ghost:hover {
      background: var(--teal);
      color: #fff
    }
 #home-page .btn-gold {
      background: var(--gold-grad);
      color: var(--ink);
      box-shadow: 0 10px 24px -8px rgba(245, 158, 11, .5)
    }
 #home-page .btn-gold:hover {
      transform: translateY(-2px);
      background: var(--gold-dark);
      box-shadow: 0 16px 32px -10px rgba(245, 158, 11, .6)
    }
 #home-page .shape {
      position: absolute;
      border-radius: 50%;
      pointer-events: none;
      z-index: 0
    }
 #home-page .shape-teal {
      background: radial-gradient(circle, rgba(38, 166, 154, .35), transparent 70%);
      filter: blur(20px)
    }
 #home-page .shape-light {
      background: radial-gradient(circle, rgba(178, 223, 219, .5), transparent 70%);
      filter: blur(30px)
    }
 #home-page .arc {
      position: absolute;
      border: 2px solid rgba(0, 137, 123, .15);
      border-radius: 50%;
      pointer-events: none
    }
 #home-page .nav {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 100;
      padding: 18px 0;
      transition: all .3s ease;
      background: transparent
    }
 #home-page .nav.scrolled {
      background: rgba(249, 250, 251, .85);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      box-shadow: 0 4px 20px -10px rgba(0, 0, 0, .08);
      padding: 12px 0
    }
 #home-page .nav-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative
    }
 #home-page .logo {
      font-family: "Calibri", "Segoe UI", sans-serif;
      font-weight: 800;
      font-size: 2rem;
      color: var(--teal);
      letter-spacing: -.02em
    }
 #home-page .logo span {
      color: var(--ink)
    }
 #home-page .nav-links {
      display: flex;
      gap: 32px;
      align-items: center
    }
 #home-page .nav-links a {
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--teal);
      transition: color .2s
    }
 #home-page .nav-links a:hover {
      color: var(--teal-dark)
    }
 #home-page .nav-links a.btn {
      color: #fff
    }
 #home-page .nav .btn {
      padding: 10px 20px;
      font-size: .88rem
    }
 #home-page .hamburger {
      display: none;
      background: none;
      border: none;
      cursor: pointer;
      width: 32px;
      height: 32px;
      flex-direction: column;
      justify-content: center;
      gap: 5px
    }
 #home-page .hamburger span {
      display: block;
      width: 22px;
      height: 2px;
      background: var(--ink);
      transition: .3s
    }
 #home-page .topbar {
      margin-top: 72px;
      background: var(--grad);
      color: #fff;
      overflow: hidden;
      position: relative;
      border-bottom: 1px solid rgba(255, 255, 255, .15)
    }
 #home-page .topbar-track {
      display: flex;
      transition: transform .7s cubic-bezier(.65, .05, .36, 1)
    }
 #home-page .topbar-slide {
      flex: 0 0 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 20px;
      padding: 18px 24px;
      text-align: center;
      font-family: "Syne", sans-serif;
      font-weight: 600;
      font-size: 1.35rem;
      letter-spacing: .01em;
      cursor: pointer;
      color: #FFF8E7;
      transition: background .2s
    }
 #home-page .topbar-slide:hover {
      background: rgba(255, 255, 255, .08)
    }
 #home-page .topbar-slide .tb-tag {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 14px;
      border-radius: 999px;
      background: #FFF8E7;
      color: var(--teal-dark);
      font-family: "Inter", sans-serif;
      font-size: .78rem;
      font-weight: 800;
      letter-spacing: .14em;
      text-transform: uppercase
    }
 #home-page .topbar-dots {
      position: absolute;
      bottom: 6px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 6px
    }
 #home-page .topbar-dots button {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      border: none;
      background: rgba(255, 255, 255, .4);
      cursor: pointer;
      padding: 0;
      transition: .25s
    }
 #home-page .topbar-dots button.active {
      background: #fff;
      width: 18px;
      border-radius: 3px
    }
@media  (max-width:600px) { #home-page .topbar-slide {
        font-size: 1.05rem;
        gap: 10px;
        flex-wrap: wrap;
        padding: 14px 16px
      }}
 #home-page .hero-form {
      background: var(--grad);
      color: #fff;
      border: none;
      border-radius: 24px;
      padding: 32px;
      box-shadow: 0 30px 80px -20px rgba(0, 137, 123, .35);
      position: relative;
      overflow: hidden
    }
 #home-page .hero-form::before {
      content: "";
      position: absolute;
      top: -100px;
      right: -100px;
      width: 250px;
      height: 250px;
      background: rgba(255, 255, 255, .08);
      border-radius: 50%
    }
 #home-page .hero-form::after {
      content: "";
      position: absolute;
      bottom: -120px;
      left: -80px;
      width: 280px;
      height: 280px;
      background: rgba(255, 255, 255, .08);
      border-radius: 50%
    }
 #home-page .hero-form h4 {
      font-family: "Syne";
      font-size: 1.2rem;
      color: #fff;
      margin-bottom: 4px;
      position: relative;
      z-index: 2
    }
 #home-page .hero-form .sub {
      color: rgba(255, 255, 255, .85);
      font-size: .85rem;
      margin-bottom: 20px;
      position: relative;
      z-index: 2
    }
 #home-page .hero-form .form-row {
      margin-bottom: 12px;
      text-align: left;
      position: relative;
      z-index: 2
    }
 #home-page .hero-form label {
      display: block;
      font-size: .78rem;
      font-weight: 600;
      color: rgba(255, 255, 255, .9);
      margin-bottom: 6px
    }
 #home-page .hero-form input, #home-page .hero-form select {
      width: 100%;
      padding: 11px 14px;
      background: rgba(255, 255, 255, .95);
      border: 1px solid rgba(255, 255, 255, .4);
      border-radius: 10px;
      font-size: .9rem;
      font-family: inherit;
      color: var(--ink);
      outline: none;
      transition: .2s
    }
 #home-page .hero-form input:focus, #home-page .hero-form select:focus {
      border-color: #fff;
      background: #fff;
      box-shadow: 0 0 0 3px rgba(255, 255, 255, .3)
    }
 #home-page .hero-form .btn-full {
      width: 100%;
      justify-content: center;
      background: var(--teal-dark);
      
      color: #fff;
      
      margin-top: 6px;
      padding: 14px;
      border-radius: 10px;
      font-weight: 700
    }
 #home-page .hero-form .btn-full:hover {
      background: var(--ink);
      color: #fff
    }
 #home-page .hero-form .reg-note {
      margin-top: 12px;
      font-size: .78rem;
      color: rgba(255, 255, 255, .75);
      text-align: center;
      position: relative;
      z-index: 2
    }
 #home-page .hero-form-success {
      display: none;
      text-align: center;
      padding: 28px 8px;
      position: relative;
      z-index: 2;
      animation: fadeInUp .5s ease forwards
    }
 #home-page .hero-form-success.visible {
      display: block
    }
 #home-page .hero-form-success .tick {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: rgba(255, 255, 255, .22);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.8rem;
      font-weight: 700;
      margin: 0 auto 18px;
      border: 2px solid rgba(255, 255, 255, .5)
    }
 #home-page .hero-form-success h4 {
      color: #fff;
      font-family: "Syne";
      font-size: 1.15rem;
      margin-bottom: 10px
    }
 #home-page .hero-form-success p {
      color: rgba(255, 255, 255, .85);
      font-size: .88rem;
      line-height: 1.6
    }
@keyframes  fadeInUp {from  {
        opacity: 0;
        transform: translateY(18px)
      }
to  {
        opacity: 1;
        transform: translateY(0)
      }}
@keyframes  floaty {0%,
      100%  {
        transform: translateY(0) rotate(0deg)
      }
50%  {
        transform: translateY(-8px) rotate(3deg)
      }}
@keyframes  ring-pulse {0%,
      100%  {
        box-shadow: 0 12px 24px -10px rgba(0, 137, 123, .4), 0 0 0 0 rgba(38, 166, 154, .45)
      }
50%  {
        box-shadow: 0 12px 24px -10px rgba(0, 137, 123, .4), 0 0 0 14px rgba(38, 166, 154, 0)
      }}
 #home-page .trainer .avatar {
      animation: floaty 4s ease-in-out infinite, ring-pulse 2.6s ease-out infinite;
      transition: transform .4s ease
    }
 #home-page .trainer:nth-child(2) .avatar {
      animation-delay: .6s, .4s
    }
 #home-page .trainer:nth-child(3) .avatar {
      animation-delay: 1.2s, .8s
    }
 #home-page .trainer:hover .avatar {
      transform: scale(1.08) rotate(-4deg)
    }
 #home-page .hero {
      background: var(--grad-dark);
      color: var(--ink);
      min-height: 100vh;
      display: flex;
      align-items: center;
      padding: 60px 0 80px;
      
      position: relative;
      overflow: hidden
    }
 #home-page .hero::before {
      content: "";
      position: absolute;
      top: -200px;
      right: -200px;
      width: 600px;
      height: 600px;
      background: radial-gradient(circle, rgba(0, 137, 123, .25), transparent 60%);
      border-radius: 50%
    }
 #home-page .hero::after {
      content: "";
      position: absolute;
      bottom: -150px;
      left: -100px;
      width: 400px;
      height: 400px;
      background: radial-gradient(circle, rgba(38, 166, 154, .18), transparent 60%);
      border-radius: 50%
    }
 #home-page .hero-grid {
      display: grid;
      grid-template-columns: 1.2fr 1fr;
      gap: 60px;
      align-items: center;
      position: relative;
      z-index: 2
    }
 #home-page .hero h1 {
      color: var(--ink);
      margin: 18px 0 22px;
      line-height: 1.35;
      
      padding-bottom: 6px;
      
    }
 #home-page .hero h1 .accent {
      background: linear-gradient(135deg, #26A69A, #00695C);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent
    }
 #home-page .hero p.lead {
      font-size: 1.1rem;
      color: var(--muted);
      max-width: 560px;
      margin-bottom: 32px
    }
 #home-page .badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: var(--grad);
      border-radius: 999px;
      font-size: .82rem;
      font-weight: 600;
      color: #fff;
      box-shadow: 0 8px 24px -8px rgba(0, 137, 123, .6)
    }
 #home-page .badge::before {
      content: "";
      width: 8px;
      height: 8px;
      background: #fff;
      border-radius: 50%;
      animation: pulse 2s infinite
    }
@keyframes  pulse {0%,
      100%  {
        opacity: 1
      }
50%  {
        opacity: .4
      }}
 #home-page .hero-ctas {
      display: flex;
      gap: 14px;
      flex-wrap: wrap
    }
 #home-page .mockup {
      background: #fff;
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid #E5E7EB;
      border-radius: 24px;
      padding: 32px;
      box-shadow: 0 30px 80px -20px rgba(0, 137, 123, .15);
      transform: rotate(-2deg);
      transition: transform .6s ease;
      position: relative
    }
 #home-page .mockup:hover {
      transform: rotate(0deg)
    }
 #home-page .mockup-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 24px
    }
 #home-page .dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: rgba(0, 137, 123, .25)
    }
 #home-page .mockup h4 {
      color: var(--ink);
      font-size: 1rem;
      margin-bottom: 6px
    }
 #home-page .mockup .sub {
      color: var(--muted);
      font-size: .82rem;
      margin-bottom: 24px
    }
 #home-page .stats-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px
    }
 #home-page .stat {
      background: #F0FDFA;
      border: 1px solid #B2DFDB;
      border-radius: 12px;
      padding: 18px
    }
 #home-page .stat .num {
      font-family: "Syne";
      font-size: 1.6rem;
      font-weight: 700;
      color: var(--teal);
      display: block;
      line-height: 1
    }
 #home-page .stat .lbl {
      color: var(--muted);
      font-size: .78rem;
      margin-top: 6px;
      display: block
    }
 #home-page #who {
      background: #fff;
      padding: 120px 0
    }
 #home-page .who-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 60px;
      align-items: start
    }
 #home-page .check-list {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 18px;
      margin-top: 28px
    }
 #home-page .check-list li {
      display: flex;
      gap: 14px;
      align-items: flex-start;
      font-size: 1rem;
      color: var(--ink)
    }
 #home-page .check-icon {
      flex-shrink: 0;
      width: 26px;
      height: 26px;
      border-radius: 50%;
      background: var(--teal-light);
      color: var(--teal-dark);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: .9rem;
      margin-top: 2px
    }
 #home-page .outcome-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 18px;
      margin-top: 28px
    }
 #home-page .outcome {
      padding: 24px;
      background: linear-gradient(135deg, #F0FDFA 0%, #fff 100%);
      border: 1px solid var(--teal-light);
      border-radius: var(--radius);
      transition: transform .3s, box-shadow .3s
    }
 #home-page .outcome:hover {
      transform: translateY(-4px);
      box-shadow: var(--shadow)
    }
 #home-page .outcome .big {
      font-family: "Calibri", "Segoe UI", sans-serif;
      font-size: 2rem;
      font-weight: 800;
      color: var(--teal);
      display: block;
      line-height: 1;
      margin-bottom: 8px
    }
 #home-page .outcome .desc {
      font-size: .88rem;
      color: var(--ink-soft);
      font-weight: 500
    }
 #home-page #achieve {
      background: #E0F2F1;
      color: var(--ink);
      position: relative
    }
 #home-page #achieve h2 {
      color: var(--ink)
    }
 #home-page #achieve .eyebrow {
      color: var(--teal)
    }
 #home-page #achieve .section-sub {
      color: var(--ink-soft);
      max-width: 600px;
      margin-bottom: 48px
    }
 #home-page .ach-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 22px
    }
 #home-page .ach-card {
      padding: 32px 28px;
      background: #fff;
      border: 1px solid #C8E6C9;
      border-radius: var(--radius);
      transition: all .35s ease;
      position: relative;
      overflow: hidden
    }
 #home-page .ach-card::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: var(--grad);
      transform: scaleX(0);
      transform-origin: left;
      transition: transform .4s
    }
 #home-page .ach-card:hover {
      background: #fff;
      border-color: var(--teal);
      transform: translateY(-6px);
      box-shadow: var(--shadow)
    }
 #home-page .ach-card:hover::before {
      transform: scaleX(1)
    }
 #home-page .ach-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      background: var(--grad);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 20px;
      font-size: 1.4rem
    }
 #home-page .ach-card h3 {
      color: var(--ink);
      margin-bottom: 8px;
      font-size: 1.15rem
    }
 #home-page .ach-card p {
      color: var(--muted);
      font-size: .92rem
    }
 #home-page #curriculum {
      background: var(--off)
    }
 #home-page .modules {
      max-width: 880px;
      margin: 40px auto 0;
      display: flex;
      flex-direction: column;
      gap: 12px
    }
 #home-page .module {
      background: #fff;
      border: 1px solid #E5E7EB;
      border-radius: 14px;
      overflow: hidden;
      transition: box-shadow .25s, border-color .25s
    }
 #home-page .module.open {
      box-shadow: 0 10px 30px -12px rgba(0, 137, 123, .25);
      border-color: var(--teal-light)
    }
 #home-page .module-head {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 22px 26px;
      cursor: pointer;
      user-select: none
    }
 #home-page .module-num {
      flex-shrink: 0;
      width: 42px;
      height: 42px;
      border-radius: 10px;
      background: var(--teal-light);
      color: var(--teal-dark);
      font-family: "Syne";
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
      transition: .3s
    }
 #home-page .module.open .module-num {
      background: var(--teal);
      color: #fff
    }
 #home-page .module-title {
      flex: 1;
      font-weight: 600;
      font-size: 1.02rem;
      color: var(--ink);
      font-family: "Syne"
    }
 #home-page .chev {
      width: 22px;
      height: 22px;
      color: var(--teal);
      transition: transform .3s
    }
 #home-page .module.open .chev {
      transform: rotate(180deg)
    }
 #home-page .module-body {
      max-height: 0;
      overflow: hidden;
      transition: max-height .35s ease, padding .35s ease;
      padding: 0 26px 0 88px
    }
 #home-page .module.open .module-body {
      max-height: 200px;
      padding-bottom: 22px
    }
 #home-page .module-body p {
      font-size: .93rem;
      line-height: 1.65
    }
 #home-page #register {
      background: var(--grad);
      color: #fff;
      position: relative;
      overflow: hidden
    }
 #home-page #register::before, #home-page #register::after {
      content: "";
      position: absolute;
      border-radius: 50%
    }
 #home-page #register::before {
      top: -150px;
      left: -100px;
      width: 400px;
      height: 400px;
      background: rgba(255, 255, 255, .08)
    }
 #home-page #register::after {
      bottom: -200px;
      right: -150px;
      width: 500px;
      height: 500px;
      background: rgba(178, 223, 219, .15)
    }
 #home-page .reg-wrap {
      max-width: 560px;
      margin: 0 auto;
      position: relative;
      z-index: 2;
      text-align: center
    }
 #home-page .reg-wrap h2 {
      color: #fff;
      margin-bottom: 12px
    }
 #home-page .reg-wrap .sub {
      color: rgba(255, 255, 255, .85);
      margin-bottom: 36px
    }
 #home-page .glass-card {
      background: rgba(255, 255, 255, .12);
      backdrop-filter: blur(24px);
      -webkit-backdrop-filter: blur(24px);
      border: 1px solid rgba(255, 255, 255, .25);
      border-radius: 24px;
      padding: 40px;
      box-shadow: 0 30px 80px -20px rgba(0, 0, 0, .3)
    }
 #home-page .form-row {
      margin-bottom: 16px;
      text-align: left
    }
 #home-page .form-row label {
      display: block;
      font-size: .82rem;
      font-weight: 600;
      color: rgba(255, 255, 255, .9);
      margin-bottom: 8px
    }
 #home-page .form-row input, #home-page .form-row select {
      width: 100%;
      padding: 13px 16px;
      background: rgba(255, 255, 255, .95);
      border: 1px solid rgba(255, 255, 255, .4);
      border-radius: 10px;
      font-size: .95rem;
      font-family: inherit;
      color: var(--ink);
      outline: none;
      transition: .2s
    }
 #home-page .form-row input:focus, #home-page .form-row select:focus {
      border-color: #fff;
      box-shadow: 0 0 0 3px rgba(255, 255, 255, .25)
    }
 #home-page .btn-full {
      width: 100%;
      justify-content: center;
      background: var(--teal-dark);
      color: #fff;
      margin-top: 8px;
      padding: 16px
    }
 #home-page .btn-full:hover {
      background: #000
    }
 #home-page .reg-note {
      margin-top: 18px;
      font-size: .82rem;
      color: rgba(255, 255, 255, .8)
    }
 #home-page #trainers {
      background: #fff
    }
 #home-page .trainer-marquee {
      margin-top: 48px;
      overflow: hidden;
      position: relative;
      -webkit-mask-image: linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent);
      mask-image: linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent)
    }
 #home-page .trainer-marquee:hover .trainer-grid {
      animation-play-state: paused
    }
 #home-page .trainer-grid {
      display: flex;
      gap: 24px;
      width: max-content;
      animation: trainer-scroll 50s linear infinite
    }
@keyframes  trainer-scroll {0%  {
        transform: translateX(0)
      }
100%  {
        transform: translateX(-50%)
      }}
 #home-page .trainer {
      flex: 0 0 300px;
      padding: 36px 28px;
      text-align: center;
      background: #fff;
      border: 1px solid #E5E7EB;
      border-radius: var(--radius);
      transition: all .35s ease;
      display: flex;
      flex-direction: column;
      align-items: center
    }
 #home-page .trainer:hover {
      transform: translateY(-8px);
      box-shadow: var(--shadow);
      border-color: var(--teal-light)
    }
 #home-page .avatar {
      width: 90px;
      height: 90px;
      border-radius: 50%;
      background: var(--grad);
      color: #fff;
      font-family: "Syne";
      font-weight: 700;
      font-size: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
      box-shadow: 0 12px 24px -10px rgba(0, 137, 123, .4)
    }
 #home-page .trainer h3 {
      margin-bottom: 6px
    }
 #home-page .trainer .role {
      color: var(--teal);
      font-size: .85rem;
      font-weight: 600;
      margin-bottom: 14px
    }
 #home-page .trainer .bio {
      font-size: .9rem;
      margin-bottom: 18px;
      flex: 1 1 auto
    }
 #home-page .lnk {
      display: inline-flex;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: var(--teal-light);
      color: var(--teal-dark);
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: .82rem;
      transition: .25s
    }
 #home-page .lnk:hover {
      background: var(--teal);
      color: #fff
    }
 #home-page #expect {
      background: var(--off)
    }
 #home-page .expect-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
      margin-top: 48px
    }
 #home-page .expect {
      padding: 28px;
      background: #fff;
      border-radius: var(--radius);
      text-align: center;
      position: relative;
      border: 1px solid #EEF2F7;
      transition: .3s
    }
 #home-page .expect::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 3px;
      background: var(--teal);
      transition: width .35s ease
    }
 #home-page .expect:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 28px -16px rgba(0, 137, 123, .3)
    }
 #home-page .expect:hover::after {
      width: 60%
    }
 #home-page .expect-icon {
      width: 54px;
      height: 54px;
      border-radius: 14px;
      background: var(--grad);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 16px;
      font-size: 1.4rem
    }
 #home-page .expect-icon svg {
      width: 26px;
      height: 26px;
      stroke: #fff
    }
 #home-page .expect h4 {
      font-size: 1rem;
      margin-bottom: 6px
    }
 #home-page .expect p {
      font-size: .85rem
    }
 #home-page #pricing {
      background: #fff
    }
 #home-page .price-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 24px;
      margin-top: 48px;
      align-items: stretch
    }
 #home-page .price-grid-2col {
      grid-template-columns: repeat(2, 1fr);
      max-width: 900px;
      margin-left: auto;
      margin-right: auto
    }
 #home-page .price {
      padding: 36px 30px;
      background: #fff;
      border: 1px solid #E5E7EB;
      border-radius: 20px;
      display: flex;
      flex-direction: column;
      transition: .3s;
      position: relative
    }
 #home-page .price:hover {
      transform: translateY(-6px);
      box-shadow: var(--shadow)
    }
 #home-page .price.featured {
      background: var(--grad);
      color: #fff;
      border: none;
      transform: scale(1.04);
      box-shadow: 0 30px 60px -20px rgba(0, 137, 123, .5)
    }
 #home-page .price.featured:hover {
      transform: scale(1.04) translateY(-6px)
    }
 #home-page .price.featured h3, #home-page .price.featured .amt, #home-page .price.featured li {
      color: #fff
    }
 #home-page .price.featured .feat-list li::before {
      color: #fff
    }
 #home-page .popular {
      position: absolute;
      top: -18px;
      left: 50%;
      transform: translateX(-50%);
      background: var(--gold-grad);
      color: #fff;
      padding: 9px 22px;
      border-radius: 999px;
      font-family: "Inter", sans-serif;
      font-size: .85rem;
      font-weight: 700;
      letter-spacing: .1em;
      text-transform: uppercase;
      box-shadow: 0 10px 24px -8px rgba(245, 158, 11, .55);
      white-space: nowrap
    }
 #home-page .price.student {
      border-color: #A8DADC
    }
 #home-page .price.student h3 {
      color: #2B7A8D
    }
 #home-page .price.student .amt {
      color: #2B7A8D
    }
 #home-page .price.student.featured {
      background: linear-gradient(135deg, #3A9AAD 0%, #5EBAC9 55%, #84D3DE 100%);
      box-shadow: 0 30px 60px -20px rgba(43, 122, 141, .4)
    }
 #home-page .price.student.featured h3, #home-page .price.student.featured .amt, #home-page .price.student.featured li {
      color: #fff
    }
 #home-page .price.student .btn-ghost {
      border-color: #3A9AAD;
      color: #2B7A8D
    }
 #home-page .price.student .btn-ghost:hover {
      background: #3A9AAD;
      color: #fff
    }
 #home-page .price.student.featured .btn-primary {
      background: #fff;
      color: #2B7A8D
    }
 #home-page .price.corporate {
      border-color: #B2DFDB
    }
 #home-page .price.corporate h3 {
      color: var(--teal-dark)
    }
 #home-page .price.corporate .amt {
      color: var(--teal-dark)
    }
 #home-page .price.corporate.featured {
      background: linear-gradient(135deg, #00897B 0%, #26A69A 55%, #4DB6AC 100%);
      box-shadow: 0 30px 60px -20px rgba(0, 137, 123, .5)
    }
 #home-page .price.corporate.featured h3, #home-page .price.corporate.featured .amt, #home-page .price.corporate.featured li {
      color: #fff
    }
 #home-page .price.corporate.featured .btn-primary {
      background: #fff;
      color: var(--teal-dark)
    }
 #home-page .price h3 {
      margin-bottom: 6px
    }
 #home-page .price .tag {
      font-size: .86rem;
      color: var(--muted);
      margin-bottom: 20px
    }
 #home-page .price.featured .tag {
      color: rgba(255, 255, 255, .85)
    }
 #home-page .amt {
      font-family: "Calibri", "Segoe UI", sans-serif;
      font-size: 2.4rem;
      font-weight: 800;
      color: var(--ink);
      margin-bottom: 24px;
      display: block
    }
 #home-page .amt small {
      font-size: .85rem;
      font-weight: 500;
      color: var(--muted)
    }
 #home-page .price.featured .amt small {
      color: rgba(255, 255, 255, .8)
    }
 #home-page .feat-list {
      list-style: none;
      margin-bottom: 28px;
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 12px
    }
 #home-page .feat-list li {
      font-size: .92rem;
      color: var(--ink-soft);
      padding-left: 26px;
      position: relative
    }
 #home-page .feat-list li::before {
      content: "✓";
      position: absolute;
      left: 0;
      color: var(--teal);
      font-weight: 700
    }
 #home-page .price .btn {
      width: 100%;
      justify-content: center
    }
 #home-page .price.featured .btn-primary {
      background: #fff;
      color: var(--teal-dark)
    }
 #home-page .price.featured .btn-primary:hover {
      background: var(--ink);
      color: #fff
    }
 #home-page .price-note {
      text-align: center;
      margin-top: 28px;
      font-size: .88rem;
      color: var(--muted)
    }
 #home-page #faq {
      background: var(--off)
    }
 #home-page .faq-list {
      max-width: 780px;
      margin: 48px auto 0;
      display: flex;
      flex-direction: column;
      gap: 12px
    }
 #home-page .faq {
      background: #fff;
      border: 1px solid #E5E7EB;
      border-radius: 12px;
      overflow: hidden;
      transition: .25s
    }
 #home-page .faq.open {
      border-color: var(--teal-light);
      box-shadow: 0 8px 24px -14px rgba(0, 137, 123, .3)
    }
 #home-page .faq-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px 24px;
      cursor: pointer;
      font-weight: 600;
      color: var(--ink);
      font-size: .98rem
    }
 #home-page .faq-head .chev {
      transition: transform .3s;
      color: var(--teal);
      font-size: 1.2rem;
      font-weight: 700
    }
 #home-page .faq.open .chev {
      transform: rotate(180deg)
    }
 #home-page .faq-body {
      max-height: 0;
      overflow: hidden;
      transition: max-height .35s ease, padding .35s ease;
      padding: 0 24px
    }
 #home-page .faq.open .faq-body {
      max-height: 240px;
      padding: 0 24px 22px
    }
 #home-page .faq-body p {
      font-size: .92rem;
      line-height: 1.7
    }
 #home-page footer {
      background: #E0F2F1;
      color: var(--ink);
      padding: 60px 0 30px;
      position: relative
    }
 #home-page footer::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: var(--grad)
    }
 #home-page .foot-grid {
      display: grid;
      grid-template-columns: 1.5fr 1fr 1fr 1fr;
      gap: 40px;
      margin-bottom: 40px
    }
 #home-page .foot-brand p {
      margin-top: 14px;
      font-size: .9rem;
      max-width: 300px;
      color: var(--muted)
    }
 #home-page .foot-col h5 {
      color: var(--ink);
      font-family: "Syne";
      font-size: .95rem;
      margin-bottom: 16px
    }
 #home-page .foot-col ul {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 10px
    }
 #home-page .foot-col a {
      font-size: .88rem;
      color: var(--muted);
      transition: color .2s
    }
 #home-page .foot-col a:hover {
      color: var(--teal)
    }
 #home-page .socials {
      display: flex;
      gap: 10px;
      margin-top: 14px
    }
 #home-page .socials a {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: var(--teal-light);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--teal-dark);
      font-size: .85rem;
      font-weight: 700;
      transition: .25s
    }
 #home-page .socials a:hover {
      background: var(--teal);
      color: #fff;
      transform: translateY(-2px)
    }
 #home-page .foot-bottom {
      border-top: 1px solid rgba(0, 137, 123, .15);
      padding-top: 24px;
      text-align: center;
      font-size: .82rem;
      color: var(--muted)
    }
 #home-page #page-loader {
      position: fixed;
      inset: 0;
      z-index: 9999;
      background: var(--off);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 22px;
      transition: opacity .5s ease, visibility .5s ease
    }
 #home-page #page-loader.hidden {
      opacity: 0;
      visibility: hidden;
      pointer-events: none
    }
 #home-page #page-loader .loader-logo {
      width: 160px;
      height: 160px;
      object-fit: contain;
      animation: loader-bob 1.6s ease-in-out infinite
    }
 #home-page #page-loader .loader-ring {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      border: 3px solid var(--teal-light);
      border-top-color: var(--teal);
      animation: loader-spin .9s linear infinite
    }
 #home-page #page-loader .loader-text {
      font-family: "Syne", sans-serif;
      font-weight: 600;
      color: var(--teal-dark);
      letter-spacing: .14em;
      text-transform: uppercase;
      font-size: .78rem
    }
@keyframes  loader-spin {to  {
        transform: rotate(360deg)
      }}
@keyframes  loader-bob {0%,
      100%  {
        transform: translateY(0)
      }
50%  {
        transform: translateY(-8px)
      }}
 #home-page .reveal {
      opacity: 0;
      transform: translateY(30px);
      transition: opacity .8s ease, transform .8s ease
    }
 #home-page .reveal.visible {
      opacity: 1;
      transform: translateY(0)
    }
 #home-page .section-head {
      text-align: center;
      max-width: 680px;
      margin: 0 auto 20px
    }
 #home-page .section-sub {
      color: var(--muted);
      font-size: 1rem;
      max-width: 600px;
      margin: 14px auto 0
    }
@media  (max-width:900px) { #home-page .hero-grid, #home-page .who-grid {
        grid-template-columns: 1fr;
        gap: 48px
      }
 #home-page .hero-grid>div:first-child img {
        transform: scale(1.6) !important;
        margin-left: -20px !important;
        max-width: 62.5% !important;
      }
 #home-page .hero-grid>div:first-child {
        text-align: center;
        overflow: hidden
      }
 #home-page .hero-grid>div:first-child img[alt="PeopleProcess logo"] {
        margin-left: auto !important;
        margin-right: auto !important;
        transform: scale(1.6) !important;
      }
 #home-page .hero p.lead {
        margin-left: auto;
        margin-right: auto;
      }
 #home-page .hero-ctas {
        justify-content: center;
      }
 #home-page .hero-form {
        max-width: 100% !important;
        margin-left: 0 !important;
      }
 #home-page .ach-grid, #home-page .expect-grid, #home-page .price-grid {
        grid-template-columns: 1fr
      }
 #home-page .price.featured {
        transform: none
      }
 #home-page .price.featured:hover {
        transform: translateY(-6px)
      }
 #home-page .outcome-grid {
        grid-template-columns: 1fr 1fr
      }
 #home-page .foot-grid {
        grid-template-columns: 1fr 1fr;
        gap: 32px
      }
 #home-page .nav-inner {
        justify-content: center
      }
 #home-page .nav-inner .logo {
        margin: 0 auto
      }
 #home-page .nav-links {
        display: none;
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: #fff;
        flex-direction: column;
        padding: 20px;
        gap: 18px;
        box-shadow: 0 10px 30px -10px rgba(0, 0, 0, .1)
      }
 #home-page .nav-links.open {
        display: flex
      }
 #home-page .hamburger {
        display: flex;
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%)
      }
 #home-page .mockup {
        transform: none
      }}
@media  (max-width:520px) { #home-page .outcome-grid, #home-page .foot-grid {
        grid-template-columns: 1fr
      }
 #home-page .glass-card {
        padding: 28px 22px
      }}
`;

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Syne:wght@500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap";

export default function HomePage() {
  const [navOpen, setNavOpen] = useState(false);
  const [loaderHidden, setLoaderHidden] = useState(false);
  const [heroSubmitted, setHeroSubmitted] = useState(false);
  const [heroSending, setHeroSending] = useState(false);
  const rootRef = useRef(null);

  // Load the Syne/Inter fonts used by the microsite.
  useEffect(() => {
    if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);

  // Page loader hide-on-load (was window 'load' + fallback timeout).
  useEffect(() => {
    const showTimer = setTimeout(() => setLoaderHidden(true), 300);
    const fallbackTimer = setTimeout(() => setLoaderHidden(true), 4000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(fallbackTimer);
    };
  }, []);

  // Nav background on scroll (was a 'scroll' listener toggling .scrolled).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const nav = root.querySelector("#nav");
    if (!nav) return;
    const onScroll = () => {
      nav.classList.toggle("scrolled", window.scrollY > 40);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Reveal-on-scroll animation (was a manual IntersectionObserver).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const targets = root.querySelectorAll(".reveal");

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            setTimeout(() => el.classList.add("visible"), i * 60);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.12 },
    );

    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Module accordion (single-open) — was querySelectorAll('#modules .module').
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const modules = root.querySelectorAll("#modules .module");
    const cleanups: Array<() => void> = [];
    modules.forEach((m) => {
      const head = m.querySelector(".module-head");
      if (!head) return;
      const onClick = () => {
        const wasOpen = m.classList.contains("open");
        modules.forEach((x) => x.classList.remove("open"));
        if (!wasOpen) m.classList.add("open");
      };
      head.addEventListener("click", onClick);
      cleanups.push(() => head.removeEventListener("click", onClick));
    });
    return () => cleanups.forEach((c) => c());
  }, []);

  // FAQ accordion (independent toggles) — was querySelectorAll('#faqList .faq').
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const faqs = root.querySelectorAll("#faqList .faq");
    const cleanups: Array<() => void> = [];
    faqs.forEach((f) => {
      const head = f.querySelector(".faq-head");
      if (!head) return;
      const onClick = () => f.classList.toggle("open");
      head.addEventListener("click", onClick);
      cleanups.push(() => head.removeEventListener("click", onClick));
    });
    return () => cleanups.forEach((c) => c());
  }, []);

  // Top announcement carousel — was the IIFE building dots + setInterval.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const track = root.querySelector("#topbarTrack");
    const dotsWrap = root.querySelector("#topbarDots");
    if (!track || !dotsWrap) return;

    dotsWrap.innerHTML = "";
    const slides = track.children.length;
    let i = 0;
    let timer: ReturnType<typeof setInterval>;

    const render = () => {
      track.style.transform = `translateX(-${i * 100}%)`;
      Array.from(dotsWrap.children).forEach((d, k) =>
        d.classList.toggle("active", k === i),
      );
    };
    const next = () => {
      i = (i + 1) % slides;
      render();
    };
    const reset = () => {
      clearInterval(timer);
      timer = setInterval(next, 4500);
    };

    for (let k = 0; k < slides; k++) {
      const b = document.createElement("button");
      b.setAttribute("aria-label", `Slide ${k + 1}`);
      b.addEventListener("click", () => {
        i = k;
        render();
        reset();
      });
      dotsWrap.appendChild(b);
    }

    timer = setInterval(next, 4500);
    render();

    return () => clearInterval(timer);
  }, []);

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setHeroSending(true);
    const formData = new FormData(e.currentTarget);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setHeroSubmitted(true);
        e.currentTarget.reset();
      } else {
        setHeroSending(false);
        alert("Something went wrong, please try again.");
      }
    } catch (error) {
      setHeroSending(false);
      alert("Something went wrong, please try again.");
    }
  };

  const closeNav = () => setNavOpen(false);

  return (
    <div id="home-page" ref={rootRef}>
      <style>{HOME_STYLES}</style>
<div id="page-loader" role="status" aria-label="Loading" className={loaderHidden ? 'hidden' : ''}><img className="loader-logo" src="/microsite/images/logo.png" alt="Loading" /><div className="loader-ring" /><div className="loader-text">Loading…</div></div>
<nav className="nav" id="nav"><div className="container nav-inner"><a href="#" className="logo" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><img src="/microsite/images/mentesquare-logo.png" alt="MenteSquare logo" style={{ width: '64px', height: '64px', objectFit: 'contain' }} />
        MenteSquare
      </a><div className={`nav-links${navOpen ? ' open' : ''}`} id="navLinks"><a href="#who" onClick={closeNav}>About</a><a href="#curriculum" onClick={closeNav}>Curriculum</a><a href="#trainers" onClick={closeNav}>Trainers</a><a href="#pricing" onClick={closeNav}>Pricing</a><a href="#faq" onClick={closeNav}>FAQs</a><a href="/contact" className="btn btn-primary" onClick={closeNav}>Contact</a></div><button className="hamburger" id="hamburger" aria-label="Menu" onClick={() => setNavOpen((open) => !open)}><span /><span /><span /></button></div></nav>
<div className="topbar" id="topbar" aria-label="Announcements"><div className="topbar-track" id="topbarTrack"><a href="#pricing" className="topbar-slide"><span className="tb-tag">Early Bird Offer</span><span>Save up to ₹3,000 —
          Early bird pricing from <strong>₹15,500</strong> (incl. GST).</span></a><a href="#trainers" className="topbar-slide"><span className="tb-tag">Faculty</span><span>Learn from 11 international
          specialists — Dr. Stephen Benton, Anamika Viswanathan, Kate NG & more.</span></a><a href="#reserve-seat" className="topbar-slide"><span className="tb-tag">Batch Starts</span><span>September 2026 ·
          Weekend
          evenings. <span style={{ textDecoration: 'underline' }}>Reserve seat →</span></span></a></div><div className="topbar-dots" id="topbarDots" /></div>
<section className="hero" id="reserve-seat"><div className="container"><div className="badge">New Cohort · September 2026</div><div className="hero-grid"><div><img src="/microsite/images/logo.png" alt="PeopleProcess logo" style={{ width: '96px', height: '96px', objectFit: 'contain', display: 'block', margin: '10px 0 6px -100px', transform: 'scale(2.4)', transformOrigin: 'left center' }} /><h1 style={{ margin: '0 0 10px', lineHeight: '1.3', paddingTop: '6px' }}>PeopleProcess:<br />Business Psychology<br />in
            Action</h1><p style={{ fontFamily: '\'Syne\',sans-serif', fontWeight: '600', fontSize: '1.15rem', color: 'var(--teal-dark)', margin: '0 0 18px', letterSpacing: '.01em' }}>
            PEOPLE. PERFORMANCE. POSSIBILITIES.</p><p className="lead">PeopleProcess is a premier live, online certification programme for individuals who want to
            understand the science behind human behaviour, performance, leadership and organisational success. Rather
            than asking you to pick a specialisation upfront, it gives you a rare, comprehensive view of the Business
            and Industrial-Organisational Psychology landscape — so you can choose your path with confidence.</p><div className="hero-ctas"><a href="https://forms.gle/ckRThBQyEpUayAWF7" target="_blank" rel="noopener" className="btn btn-gold">Enroll
              Now →</a><a href="#who" className="btn btn-outline">Know More</a></div></div><div className="hero-form" style={{ maxWidth: '360px', padding: '24px', marginLeft: 'auto' }}><form id="heroRegister" onSubmit={handleRegisterSubmit} style={{ position: 'relative', zIndex: 2, display: heroSubmitted ? 'none' : 'block' }}><h4>Reserve Your Seat</h4><p className="sub">Batch starts September 2026 · Limited seats.</p><div className="form-row"><label>Full Name</label><input type="text" name="name" required placeholder="Your full name" /></div><div className="form-row"><label>Email Address</label><input type="email" name="email" required placeholder="you@email.com" /></div><div className="form-row"><label>You Are A</label><select name="profile" required><option value="">Select your profile</option><option>Student</option><option>Student (Early Bird)</option><option>Corporate Professional</option><option>Corporate Professional (Early Bird)</option></select></div><input type="hidden" name="access_key" defaultValue="cda75346-0fe0-4ff6-b89a-42ab21b0fdaf" /><input type="hidden" name="subject" defaultValue="New Reserve Your Seat submission" /><button type="submit" className="btn btn-full" id="registerBtn" disabled={heroSending}>{heroSending ? 'Sending...' : 'Reserve My Spot →'}</button><p className="reg-note">Limited seats per cohort.</p></form><div className={`hero-form-success${heroSubmitted ? ' visible' : ''}`} id="heroSuccess"><div className="tick">✓</div><h4>You're in! We'll reach out within 24 hours.</h4><p>In the meantime, feel free to email us directly at<br />info@mentesquare.com.</p></div></div></div></div></section>
<section className="section" id="who"><div className="shape shape-light" style={{ width: '260px', height: '260px', top: '10%', right: '-60px' }} /><div className="container"><div className="who-grid"><div className="reveal"><span className="eyebrow">Who Is This For?</span><h2>Gain clarity before you commit to a specialisation.</h2><ul className="check-list"><li><span className="check-icon">✓</span><span><strong>Students</strong> studying Psychology, HR, Management or
                a related field — explore the diverse domains of Business Psychology before choosing a
                specialisation.</span></li><li><span className="check-icon">✓</span><span><strong>HR Professionals</strong> wanting a deeper understanding
                of leadership, behaviour, engagement and culture to go beyond traditional HR.</span></li><li><span className="check-icon">✓</span><span><strong>Mid-Career Professionals</strong> considering a move into
                coaching, consulting, L&D or organisational effectiveness — explore before investing in specialist
                certifications.</span></li><li><span className="check-icon">✓</span><span><strong>Practitioners</strong> looking to broaden their
                expertise, discover new areas of practice and gain perspectives from international specialists.</span></li></ul></div><div className="reveal"><span className="eyebrow">Programme Snapshot</span><h2>30 hours, 8 domains, one certification.</h2><div className="outcome-grid"><div className="outcome"><span className="big">3</span><span className="desc">Months Duration — 30 hours of live,
                interactive instruction.</span></div><div className="outcome"><span className="big">8</span><span className="desc">Specialist-taught domains across Business
                Psychology.</span></div><div className="outcome"><span className="big">11</span><span className="desc">International faculty members across
                coaching, L&D and OD.</span></div><div className="outcome"><span className="big">1:1</span><span className="desc">Personal coaching session on your way
                forward.</span></div></div></div></div></div></section>
<section className="section" id="achieve"><div className="container"><div className="section-head reveal"><span className="eyebrow">What Will You Achieve?</span><h2>Practical, portable competencies — not just theory.</h2><p className="section-sub">Six outcomes that compound across your career, whatever specialisation you choose next.
        </p></div><div className="ach-grid"><div className="ach-card reveal"><div className="ach-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#fff' }}><path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588 4 4 0 0 0 7.636 2.106 3.2 3.2 0 0 0 .164-.546c.028-.13.06-.264.097-.405.14-.495.243-.923.27-1.27.02-.243-.013-.612-.32-.853a2 2 0 0 0-.748-.353c-.253-.054-.625-.062-1.04-.04l-.123.007a1 1 0 0 1-.866-.5 1.5 1.5 0 0 1 .27-1.9c.434-.363 1.024-.6 1.634-.78l.247-.074a5 5 0 0 0 1.1-.52c.24-.168.44-.368.596-.596.28-.424.36-.93.223-1.417-.112-.39-.36-.72-.71-.95l-.172-.1c-.48-.287-1.02-.48-1.58-.56a1 1 0 0 1-.86-1.02c0-.48.38-.87.86-.87z" /><path d="M15.5 12a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7z" /></svg></div><h3>Navigate the Full Landscape</h3><p>Map the full landscape of Business Psychology and articulate the role of each domain.</p></div><div className="ach-card reveal"><div className="ach-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#fff' }}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg></div><h3>Find Your Aptitude</h3><p>Identify your strongest area of aptitude and make an evidence-based specialisation decision.</p></div><div className="ach-card reveal"><div className="ach-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#fff' }}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg></div><h3>Apply Real Frameworks</h3><p>Apply psychological frameworks to real organisational challenges — coaching, OD, leadership, assessment.
          </p></div><div className="ach-card reveal"><div className="ach-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#fff' }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg></div><h3>Work Globally</h3><p>Work with a global perspective, understanding cross-cultural and international practice contexts.</p></div><div className="ach-card reveal"><div className="ach-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#fff' }}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg></div><h3>Build Your Network</h3><p>Build a peer and faculty network across geographies and disciplines.</p></div><div className="ach-card reveal"><div className="ach-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#fff' }}><path d="M12 22v-9m0 0a5 5 0 0 1 5-5h2a5 5 0 0 0 5-5c0-2-1-3.5-2.5-4.5" /><path d="M12 13a5 5 0 0 0-5-5H5a5 5 0 0 1-5-5c0-2 1-3.5 2.5-4.5" /></svg></div><h3>Earn Your Certification</h3><p>Hold a recognised certification demonstrating multi-domain Business Psychology competence.</p></div></div></div></section>
<section className="section" id="curriculum"><div className="container"><div className="section-head reveal"><span className="eyebrow">Curriculum</span><h2>What Will You Study?</h2><p className="section-sub"><strong style={{ color: 'var(--teal)' }}>Course 1:</strong> Foundations of Business Psychology —
          built through experiential learning in coaching, facilitation, design thinking, personal branding, wellbeing
          and psychometric applications.</p></div><div className="modules reveal" id="modules"><div className="module"><div className="module-head"><div className="module-num">01</div><div className="module-title">Essentials of Business Psychology</div><svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg></div><div className="module-body"><p>A comprehensive introduction to applying psychological principles in organisational settings —
              motivation, communication, decision-making, culture, leadership styles and employee wellbeing.</p></div></div><div className="module"><div className="module-head"><div className="module-num">02</div><div className="module-title">Facilitation Skills</div><svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg></div><div className="module-body"><p>Develop the competencies to guide group interactions and collaborative activities — active listening,
              group dynamics, problem-solving and leading inclusive, productive sessions.</p></div></div><div className="module"><div className="module-head"><div className="module-num">03</div><div className="module-title">Coaching in Action</div><svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg></div><div className="module-body"><p>Core coaching principles and practice — active listening, powerful questioning, constructive feedback and
              goal-setting, applied to confidence, resilience and behavioural change.</p></div></div><div className="module"><div className="module-head"><div className="module-num">04</div><div className="module-title">Design Thinking & Business Strategy Planning</div><svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg></div><div className="module-body"><p>Combines design thinking — empathise, define, ideate, prototype, test — with strategic planning that
              aligns innovation with long-term business objectives.</p></div></div><div className="module"><div className="module-head"><div className="module-num">05</div><div className="module-title">Personal Branding & Digital Marketing</div><svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg></div><div className="module-body"><p>Build a strong professional identity and communicate your value online — social media engagement, content
              creation, audience targeting and authentic personal branding.</p></div></div><div className="module"><div className="module-head"><div className="module-num">06</div><div className="module-title">Hypnotherapy in Mental Well-being</div><svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg></div><div className="module-body"><p>3 hours of learning plus 2 hours of practice to identify and manage stress, develop strategies to perform
              better at work and become a stronger decision-maker.</p></div></div><div className="module"><div className="module-head"><div className="module-num">07</div><div className="module-title">Role of Psychometrics in HR</div><svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg></div><div className="module-body"><p>From the foundations of psychometrics through personality and cognitive ability testing to
              strengths-based development and Situational Judgment Tests, with a hands-on practical session.</p></div></div><div className="module"><div className="module-head"><div className="module-num">08</div><div className="module-title">Psychometric Immersion: Lumina Learning</div><svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg></div><div className="module-body"><p>5 hours combining theory with hands-on practice of the Lumina Learning tool for self-awareness, team
              working and behavioural dynamics.</p></div></div><div className="module"><div className="module-head"><div className="module-num">09</div><div className="module-title">Wrap-Up Session & Way Forward: Q&A</div><svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg></div><div className="module-body"><p>A highly interactive 5-hour live, in-person session in Bengaluru to synthesise concepts and arrive at
              actionable methods for career advancement.</p></div></div><div className="module"><div className="module-head"><div className="module-num">10</div><div className="module-title">One-on-One Coaching on Way Forward</div><svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg></div><div className="module-body"><p>2 hours of individual coaching per participant to develop and execute a concrete action plan tied to your
              own career goals.</p></div></div></div></div></section>
<section className="section" id="trainers"><div className="container"><div className="section-head reveal"><span className="eyebrow">Meet Your Trainers</span><h2>Best-in-class specialists, under one umbrella.</h2><p className="section-sub">11 international faculty across coaching psychology, leadership development,
          organisational effectiveness, psychological assessment, wellbeing and cross-cultural practice.</p></div><div className="trainer-marquee"><div className="trainer-grid"><div className="trainer"><img className="avatar" src="/microsite/images/stephen.jpg" alt="Dr. Stephen Benton" style={{ objectFit: 'cover' }} /><h3>Dr. Stephen Benton</h3><div className="role">Founder, BPsy Ltd · Professor Emeritus</div><p className="bio">The UK's first Professor of Business Psychology (2009). Created the world's first M.Sc.
              Business Psychology programme in 1998 and led it at the University of Westminster for nearly two decades.
            </p><a className="lnk" href="https://www.linkedin.com/in/dr-stephen-benton-c-sci-c-psychol-m-i-o-a-bb431a184/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer"><img className="avatar" src="/microsite/images/uday.jpg" alt="Udaysimha Mysore" style={{ objectFit: 'cover' }} /><h3>Udaysimha Mysore</h3><div className="role">Fractional CMO · Growth Marketing</div><p className="bio">30+ years in technology marketing across Intel, Forrester Research and greytHR. Currently
              Fractional CMO for iTuring.ai, specialising in growth and ABM strategy.</p><a className="lnk" href="https://www.linkedin.com/in/udaysimha/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer"><img className="avatar" src="/microsite/images/anamika.jpg" alt="Anamika V" style={{ objectFit: 'cover' }} /><h3>Anamika V</h3><div className="role">Founder Mentor, MenteSquare</div><p className="bio">30+ years across Business Psychology and advertising. Former Consulting Head at CHRIST
              University, where she established CAPS. NLP Coach and Lumina Spark & Strengthscope® Practitioner.</p><a className="lnk" href="https://www.linkedin.com/in/anamika-viswanathan-24b1a03/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer"><img className="avatar" src="/microsite/images/kate.jpg" alt="Kate NG" style={{ objectFit: 'cover', objectPosition: 'top center' }} /><h3>Kate NG</h3><div className="role">Executive Coach · Leadership Educator</div><p className="bio">30+ years across Strategy Consulting, Executive Coaching and Leadership Education. Lead Coach
              at London Business School and coach pool member for the TRIUM Executive MBA.</p><a className="lnk" href="https://www.linkedin.com/in/kate-ng-5299752/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer"><img className="avatar" src="/microsite/images/sushma.png" alt="Sushma Banthia" style={{ objectFit: 'cover', objectPosition: 'top center' }} /><h3>Sushma Banthia</h3><div className="role">ICF-PCC Leadership Coach · Author</div><p className="bio">25+ years in leadership and people development. Honoured as one of Asia's Top Iconic Women of
              Inspiration (2025) and co-author of "Fast Forward to Facilitation".</p><a className="lnk" href="https://www.linkedin.com/in/sushma-banthia/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer"><img className="avatar" src="/microsite/images/arnab.jpg" alt="Arnab Chatterjee" style={{ objectFit: 'cover' }} /><h3>Arnab Chatterjee</h3><div className="role">Industry Leader & Advisory Board Member</div><p className="bio">28+ years across IBM, GE Capital and American Express. Sits on advisory boards in India,
              South Africa and the UK, and is pursuing doctoral research in Workforce Psychology.</p><a className="lnk" href="https://www.linkedin.com/in/arnab-chatterjee-01b08814/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer"><img className="avatar" src="/microsite/images/kavitha.png" alt="Kavitha Talreja" style={{ objectFit: 'cover' }} /><h3>Kavitha Talreja</h3><div className="role">L&D Strategist · Facilitator</div><p className="bio">20+ years of corporate training across IT, healthcare, banking and finance. President's Award
              winner at Fidelity; clients include Merck, Visa, TCS and Societe Generale.</p><a className="lnk" href="https://www.linkedin.com/in/kavithatalreja/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer"><img className="avatar" src="/microsite/images/ayleen.jpg" alt="Ayleen Wisudha" style={{ objectFit: 'cover' }} /><h3>Ayleen Wisudha</h3><div className="role">Business Psychologist · Lumina Practitioner</div><p className="bio">20 years as Senior Lecturer on the MSc Business Psychology programme at the University of
              Westminster. Lumina Learning Partner for Indonesia and CPBP-certified.</p><a className="lnk" href="https://www.linkedin.com/in/wisudha/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer"><img className="avatar" src="/microsite/images/sangeeta.jpg" alt="Dr. Sangeeta Mahajan" style={{ objectFit: 'cover' }} /><h3>Dr. Sangeeta Mahajan</h3><div className="role">Mental Health Activist & Educator</div><p className="bio">30 years in medicine, including as Consultant Anaesthetist at Guy's & St Thomas' NHS.
              Winston Churchill Fellow specialising in adolescent mental health and grief support.</p><a className="lnk" href="https://www.linkedin.com/in/sangeeta-mahajan-0049a9237/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer"><img className="avatar" src="/microsite/images/kritika.jpg" alt="Dr. Kritika Rastogi" style={{ objectFit: 'cover' }} /><h3>Dr. Kritika Rastogi</h3><div className="role">Associate Professor, NMIMS Mumbai</div><p className="bio">13+ years across Positive, Counselling, Indian and Educational Psychology. Former Research
              Fellow at IIT Delhi and Assistant Editor of the Journal of the Indian Academy of Applied Psychology.</p><a className="lnk" href="https://www.linkedin.com/in/dr-kritika-rastogi-1ba26179/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer"><img className="avatar" src="/microsite/images/kiran.jpg" alt="Kiran VVN" style={{ objectFit: 'cover' }} /><h3>Kiran VVN</h3><div className="role">Technology Consultant & Trainer</div><p className="bio">20+ years in enterprise software. Heads Technology Consulting at LOGON, guiding C-level
              stakeholders through complex software business models. Flexera Certified Trainer.</p><a className="lnk" href="https://www.linkedin.com/in/kiranvvn/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer" aria-hidden="true"><img className="avatar" src="/microsite/images/stephen.jpg" alt="Dr. Stephen Benton" style={{ objectFit: 'cover' }} /><h3>Dr. Stephen Benton</h3><div className="role">Founder, BPsy Ltd · Professor Emeritus</div><p className="bio">The UK's first Professor of Business Psychology (2009). Created the world's first M.Sc.
              Business Psychology programme in 1998 and led it at the University of Westminster for nearly two decades.
            </p><a className="lnk" href="https://www.linkedin.com/in/dr-stephen-benton-c-sci-c-psychol-m-i-o-a-bb431a184/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer" aria-hidden="true"><img className="avatar" src="/microsite/images/uday.jpg" alt="Udaysimha Mysore" style={{ objectFit: 'cover' }} /><h3>Udaysimha Mysore</h3><div className="role">Fractional CMO · Growth Marketing</div><p className="bio">30+ years in technology marketing across Intel, Forrester Research and greytHR. Currently
              Fractional CMO for iTuring.ai, specialising in growth and ABM strategy.</p><a className="lnk" href="https://www.linkedin.com/in/udaysimha/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer" aria-hidden="true"><img className="avatar" src="/microsite/images/anamika.jpg" alt="Anamika V" style={{ objectFit: 'cover' }} /><h3>Anamika V</h3><div className="role">Founder Mentor, MenteSquare</div><p className="bio">30+ years across Business Psychology and advertising. Former Consulting Head at CHRIST
              University, where she established CAPS. NLP Coach and Lumina Spark & Strengthscope® Practitioner.</p><a className="lnk" href="https://www.linkedin.com/in/anamika-viswanathan-24b1a03/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer" aria-hidden="true"><img className="avatar" src="/microsite/images/kate.jpg" alt="Kate NG" style={{ objectFit: 'cover', objectPosition: 'top center' }} /><h3>Kate NG</h3><div className="role">Executive Coach · Leadership Educator</div><p className="bio">30+ years across Strategy Consulting, Executive Coaching and Leadership Education. Lead Coach
              at London Business School and coach pool member for the TRIUM Executive MBA.</p><a className="lnk" href="https://www.linkedin.com/in/kate-ng-5299752/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer" aria-hidden="true"><img className="avatar" src="/microsite/images/sushma.png" alt="Sushma Banthia" style={{ objectFit: 'cover', objectPosition: 'top center' }} /><h3>Sushma Banthia</h3><div className="role">ICF-PCC Leadership Coach · Author</div><p className="bio">25+ years in leadership and people development. Honoured as one of Asia's Top Iconic Women of
              Inspiration (2025) and co-author of "Fast Forward to Facilitation".</p><a className="lnk" href="https://www.linkedin.com/in/sushma-banthia/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer" aria-hidden="true"><img className="avatar" src="/microsite/images/arnab.jpg" alt="Arnab Chatterjee" style={{ objectFit: 'cover' }} /><h3>Arnab Chatterjee</h3><div className="role">Industry Leader & Advisory Board Member</div><p className="bio">28+ years across IBM, GE Capital and American Express. Sits on advisory boards in India,
              South Africa and the UK, and is pursuing doctoral research in Workforce Psychology.</p><a className="lnk" href="https://www.linkedin.com/in/arnab-chatterjee-01b08814/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer" aria-hidden="true"><img className="avatar" src="/microsite/images/kavitha.png" alt="Kavitha Talreja" style={{ objectFit: 'cover' }} /><h3>Kavitha Talreja</h3><div className="role">L&D Strategist · Facilitator</div><p className="bio">20+ years of corporate training across IT, healthcare, banking and finance. President's Award
              winner at Fidelity; clients include Merck, Visa, TCS and Societe Generale.</p><a className="lnk" href="https://www.linkedin.com/in/kavithatalreja/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer" aria-hidden="true"><img className="avatar" src="/microsite/images/ayleen.jpg" alt="Ayleen Wisudha" style={{ objectFit: 'cover' }} /><h3>Ayleen Wisudha</h3><div className="role">Business Psychologist · Lumina Practitioner</div><p className="bio">20 years as Senior Lecturer on the MSc Business Psychology programme at the University of
              Westminster. Lumina Learning Partner for Indonesia and CPBP-certified.</p><a className="lnk" href="https://www.linkedin.com/in/wisudha/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer" aria-hidden="true"><img className="avatar" src="/microsite/images/sangeeta.jpg" alt="Dr. Sangeeta Mahajan" style={{ objectFit: 'cover' }} /><h3>Dr. Sangeeta Mahajan</h3><div className="role">Mental Health Activist & Educator</div><p className="bio">30 years in medicine, including as Consultant Anaesthetist at Guy's & St Thomas' NHS.
              Winston Churchill Fellow specialising in adolescent mental health and grief support.</p><a className="lnk" href="https://www.linkedin.com/in/sangeeta-mahajan-0049a9237/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer" aria-hidden="true"><img className="avatar" src="/microsite/images/kritika.jpg" alt="Dr. Kritika Rastogi" style={{ objectFit: 'cover' }} /><h3>Dr. Kritika Rastogi</h3><div className="role">Associate Professor, NMIMS Mumbai</div><p className="bio">13+ years across Positive, Counselling, Indian and Educational Psychology. Former Research
              Fellow at IIT Delhi and Assistant Editor of the Journal of the Indian Academy of Applied Psychology.</p><a className="lnk" href="https://www.linkedin.com/in/dr-kritika-rastogi-1ba26179/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div><div className="trainer" aria-hidden="true"><img className="avatar" src="/microsite/images/kiran.jpg" alt="Kiran VVN" style={{ objectFit: 'cover' }} /><h3>Kiran VVN</h3><div className="role">Technology Consultant & Trainer</div><p className="bio">20+ years in enterprise software. Heads Technology Consulting at LOGON, guiding C-level
              stakeholders through complex software business models. Flexera Certified Trainer.</p><a className="lnk" href="https://www.linkedin.com/in/kiranvvn/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div></div></div></div></section>
<section className="section" id="expect"><div className="container"><div className="section-head reveal"><span className="eyebrow">What To Expect</span><h2>A structured, high-touch learning experience.</h2></div><div className="expect-grid"><div className="expect reveal"><div className="expect-icon"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15.5 14" /></svg></div><h4>3-Month Duration</h4><p>30 hours of live, highly interactive instruction with international faculty.</p></div><div className="expect reveal"><div className="expect-icon"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2.5" y="4.5" width="19" height="13" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17.5" x2="12" y2="21" /></svg></div><h4>Online, Live</h4><p>Weekend evening sessions, plus an in-person wrap-up session at a venue in Bengaluru.</p></div><div className="expect reveal"><div className="expect-icon"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.5 3.5a2 2 0 1 1 3 1.73V8h3.27a2 2 0 1 1 0 3.46H13.5V15a2 2 0 1 1-3 1.73V11.5H6.23a2 2 0 1 1 0-3.46H10.5V5.23a2 2 0 0 1 0-1.73z" /></svg></div><h4>8 Specialist Domains</h4><p>Each module taught by a faculty member who specialises in that exact domain.</p></div><div className="expect reveal"><div className="expect-icon"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9.5 12 4l10 5.5-10 5.5L2 9.5z" /><path d="M6 12v5c0 1.5 3 3 6 3s6-1.5 6-3v-5" /><line x1="21" y1="9.5" x2="21" y2="16" /></svg></div><h4>Certification on Completion</h4><p>A professional certification plus access to Google Classroom recordings, notes and resources.</p></div></div></div></section>
<section className="section" id="pricing"><div className="container"><div className="section-head reveal"><span className="eyebrow">Course Fee</span><h2>Four pricing options to suit your profile.</h2><p className="section-sub">All prices are inclusive of GST · Payments can be split across two instalments.</p></div><div className="price-grid price-grid-2col"><div className="price student reveal"><h3>Students</h3><p className="tag">Standard admission fee</p><span className="amt">₹18,500 <small>(incl. GST)</small></span><ul className="feat-list"><li>30 hours of live instruction across 8 domains</li><li>Google Classroom recordings, notes & resources</li><li>In-person wrap-up session in Bengaluru</li><li>1-on-1 coaching on your way forward</li><li>Professional certification on completion</li></ul><a href="https://forms.gle/ckRThBQyEpUayAWF7" target="_blank" rel="noopener" className="btn btn-ghost">Enroll
            Now</a></div><div className="price student featured reveal"><div className="popular">Early Bird</div><h3>Students</h3><p className="tag">Register ahead of the batch start</p><span className="amt">₹15,500 <small>(incl. GST)</small></span><ul className="feat-list"><li>30 hours of live instruction across 8 domains</li><li>Google Classroom recordings, notes & resources</li><li>In-person wrap-up session in Bengaluru</li><li>1-on-1 coaching on your way forward</li><li>Professional certification on completion</li></ul><a href="https://forms.gle/ckRThBQyEpUayAWF7" target="_blank" rel="noopener" className="btn btn-primary">Enroll
            Now</a></div><div className="price corporate reveal"><h3>Corporate Professionals</h3><p className="tag">Standard admission fee</p><span className="amt">₹25,000 <small>(incl. GST)</small></span><ul className="feat-list"><li>30 hours of live instruction across 8 domains</li><li>Google Classroom recordings, notes & resources</li><li>In-person wrap-up session in Bengaluru</li><li>1-on-1 coaching on your way forward</li><li>Professional certification on completion</li></ul><a href="https://forms.gle/ckRThBQyEpUayAWF7" target="_blank" rel="noopener" className="btn btn-ghost">Enroll
            Now</a></div><div className="price corporate featured reveal"><div className="popular">Early Bird</div><h3>Corporate Professionals</h3><p className="tag">Register ahead of the batch start</p><span className="amt">₹22,000 <small>(incl. GST)</small></span><ul className="feat-list"><li>30 hours of live instruction across 8 domains</li><li>Google Classroom recordings, notes & resources</li><li>In-person wrap-up session in Bengaluru</li><li>1-on-1 coaching on your way forward</li><li>Professional certification on completion</li></ul><a href="https://forms.gle/ckRThBQyEpUayAWF7" target="_blank" rel="noopener" className="btn btn-primary">Enroll
            Now</a></div></div><p className="price-note">💳 An instalment payment option is available — split your fee across two payments.</p></div></section>
<section className="section" id="faq"><div className="container"><div className="section-head reveal"><span className="eyebrow">FAQs</span><h2>Everything you wanted to ask.</h2></div><div className="faq-list" id="faqList"><div className="faq"><div className="faq-head">How is this different from a Master's, diploma or specialist certification?<span className="chev">⌄</span></div><div className="faq-body"><p>Most programmes focus on a single discipline within Psychology or HR. PeopleProcess offers a broader
              perspective — coaching, leadership, assessment, organisational development, wellbeing and workplace
              behaviour — so you understand the full landscape before specialising further.</p></div></div><div className="faq"><div className="faq-head">I'm not a Psychology student. Can I still benefit?<span className="chev">⌄</span></div><div className="faq-body"><p>Absolutely. The programme is designed for students, HR professionals, practitioners and working
              professionals from diverse backgrounds. No prior Psychology qualification is required.</p></div></div><div className="faq"><div className="faq-head">Will this help me decide on a future specialisation or career path?<span className="chev">⌄</span></div><div className="faq-body"><p>Yes. A key objective is helping you gain clarity about your interests, strengths and potential career
              pathways, by learning directly from specialists across different domains.</p></div></div><div className="faq"><div className="faq-head">What makes this programme unique?<span className="chev">⌄</span></div><div className="faq-body"><p>Rather than one instructor covering one niche, you learn from a curated faculty of 11 international
              specialists, each bringing deep expertise from their own field — within a single programme.</p></div></div><div className="faq"><div className="faq-head">What will I walk away with?<span className="chev">⌄</span></div><div className="faq-body"><p>A professional certification, plus a comprehensive understanding of Business Psychology and its
              integration with HR, exposure to global best practices, valuable professional connections, and clarity on
              where to focus your future learning.</p></div></div></div></div></section>
<footer><div className="container"><div className="foot-grid"><div className="foot-brand"><div className="logo" style={{ color: '#26A69A', display: 'flex', alignItems: 'center', gap: '8px' }}><img src="/microsite/images/mentesquare-final.png" alt="MenteSquare logo" style={{ width: '56px', height: '56px', objectFit: 'contain' }} />
            MenteSquare
          </div><p>PeopleProcess: Business Psychology in Action — PEOPLE.PERFORMANCE.POSSIBILITIES</p><p style={{ marginTop: '10px', fontSize: '.85rem' }}>info@mentesquare.com<br />+91 90199 77762</p><div className="socials"><a href="https://www.instagram.com/mentesquare" target="_blank" rel="noopener" aria-label="Instagram">IG</a><a href="https://www.linkedin.com/company/mentesquare/" target="_blank" rel="noopener" aria-label="LinkedIn">in</a></div></div><div className="foot-col"><h5>Programme</h5><ul><li><a href="#who">About</a></li><li><a href="#curriculum">Curriculum</a></li><li><a href="#pricing">Pricing</a></li><li><a href="#reserve-seat">Enroll</a></li></ul></div><div className="foot-col"><h5>Company</h5><ul><li><a href="https://mentesquare.com/home" target="_blank" rel="noopener">About</a></li><li><a href="#trainers">Faculty</a></li><li><a href="https://www.linkedin.com/company/mentesquare/" target="_blank" rel="noopener">LinkedIn</a></li><li><a href="https://www.instagram.com/mentesquare" target="_blank" rel="noopener">Instagram</a></li></ul></div><div className="foot-col"><h5>Support</h5><ul><li><a href="#faq">FAQs</a></li><li><a href="mailto:info@mentesquare.com">Contact</a></li><li><a href="#reserve-seat">Register</a></li></ul></div></div><div className="foot-bottom">© 2026 MenteSquare · Made with intent in Bengaluru, India.</div></div></footer>
    </div>
  );
}
